import {useEffect, useMemo, useRef, useState} from "react";
import {useAudioLevel} from "./use-audio-level";

const RECORDING_DURATION_MS = 5000;

const getSupportedMimeType = () => {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return "";
  }

  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", ""];

  return candidates.find((mimeType) => {
    if (!mimeType) {
      return true;
    }

    return window.MediaRecorder.isTypeSupported(mimeType);
  });
};

export const useMicrophoneTest = ({ deviceId }) => {
  const [stream, setStream] = useState(null);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("Нажмите кнопку и скажите несколько слов.");
  const [recordingUrl, setRecordingUrl] = useState("");
  const recordingUrlRef = useRef("");
  const mediaRecorderRef = useRef(null);
  const recordingTimeoutRef = useRef(null);
  const shouldAutoplayRef = useRef(false);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioElementRef = useRef(null);
  const level = useAudioLevel(stream);
  const canRecord = useMemo(
    () => typeof window !== "undefined" && typeof window.MediaRecorder !== "undefined",
    []
  );

  const stopStream = () => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
    setStream(null);
  };

  const clearRecording = () => {
    if (recordingUrlRef.current) {
      URL.revokeObjectURL(recordingUrlRef.current);
    }

    recordingUrlRef.current = "";
    setRecordingUrl("");
  };

  const stopPlayback = () => {
    if (!audioElementRef.current) {
      return;
    }

    audioElementRef.current.pause();
    audioElementRef.current.currentTime = 0;
    setStatus("idle");
    setMessage("Проверка остановлена. Нажмите кнопку, чтобы записать ещё раз.");
  };

  const playRecording = async (url = recordingUrl) => {
    if (!audioElementRef.current || !url) {
      return;
    }

    audioElementRef.current.src = url;
    await audioElementRef.current.play();
    setStatus("playing");
    setMessage("Воспроизводим запись с вашего микрофона.");
  };

  const clearRecordingTimeout = () => {
    if (recordingTimeoutRef.current) {
      window.clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    clearRecordingTimeout();

    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
  };

  const startRecording = async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setStatus("error");
      setMessage("Браузер не поддерживает запись с микрофона.");
      return;
    }

    if (!canRecord) {
      setStatus("error");
      setMessage("В этом браузере нет поддержки записи через MediaRecorder.");
      return;
    }

    stopPlayback();
    stopStream();
    clearRecording();
    chunksRef.current = [];

    try {
      const nextStream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId
          ? {
              deviceId: { exact: deviceId },
              echoCancellation: true,
              noiseSuppression: false,
              autoGainControl: false,
							voiceIsolation: false,
            }
          : true,
      });

      const mimeType = getSupportedMimeType();
      const recorder = mimeType
        ? new MediaRecorder(nextStream, { mimeType })
        : new MediaRecorder(nextStream);

      recorder.ondataavailable = (event) => {
        if (event.data?.size) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });

        if (blob.size) {
          const nextUrl = URL.createObjectURL(blob);
          shouldAutoplayRef.current = true;
          setRecordingUrl(nextUrl);
          setStatus("recorded");
          setMessage("Запись готова. Сейчас вы услышите воспроизведение.");
        } else {
          setStatus("error");
          setMessage("Не удалось сохранить запись микрофона.");
        }

        stopStream();
      };

      streamRef.current = nextStream;
      mediaRecorderRef.current = recorder;
      setStream(nextStream);
      setStatus("recording");
      setMessage("Идёт запись 5 секунд. Скажите несколько слов или сыграйте фразу.");
      recorder.start();
      recordingTimeoutRef.current = window.setTimeout(() => {
        stopRecording();
      }, RECORDING_DURATION_MS);
    } catch (_) {
      clearRecordingTimeout();
      stopStream();
      setStatus("error");
      setMessage("Нет доступа к микрофону. Разрешите его в браузере.");
    }
  };

  const togglePlayback = async () => {
    if (!audioElementRef.current || !recordingUrl) {
      return;
    }

    if (status === "playing") {
      stopPlayback();
      return;
    }

    try {
      await playRecording();
    } catch (_) {
      setStatus("error");
      setMessage("Не удалось воспроизвести запись. Проверьте разрешение на звук.");
    }
  };

  const finishPlayback = () => {
    if (audioElementRef.current) {
      audioElementRef.current.currentTime = 0;
    }

    setStatus("idle");
    setMessage("Проверка завершена. Нажмите кнопку, чтобы записать ещё раз.");
  };

  useEffect(() => {
    recordingUrlRef.current = recordingUrl;
  }, [recordingUrl]);

  useEffect(() => {
    if (!recordingUrl || !shouldAutoplayRef.current) {
      return;
    }

    shouldAutoplayRef.current = false;
    if (!audioElementRef.current) {
      return;
    }

    const audioElement = audioElementRef.current;
    audioElement.pause();
    audioElement.currentTime = 0;
    audioElement.load();

    void audioElement.play()
      .then(() => {
        setStatus("playing");
        setMessage("Воспроизводим запись с вашего микрофона.");
      })
      .catch(() => {
        setStatus("recorded");
        setMessage("Запись готова. Нажмите «Воспроизвести», чтобы проверить звук.");
      });
  }, [recordingUrl]);

  useEffect(() => {
    const audioElement = audioElementRef.current;

    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }

      clearRecordingTimeout();

      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }

      streamRef.current?.getTracks?.().forEach((track) => track.stop());
      streamRef.current = null;

      if (recordingUrlRef.current) {
        URL.revokeObjectURL(recordingUrlRef.current);
      }

      recordingUrlRef.current = "";
    };
  }, []);

  return {
    level,
    status,
    message,
    recordingUrl,
    audioElementRef,
    startRecording,
    stopRecording,
    togglePlayback,
    finishPlayback,
  };
};
